import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Editor, Node, Transforms, Range, createEditor } from 'slate';
import { ReactEditor, Editable, Slate, withReact } from 'slate-react';
import { defineMessages, useIntl } from 'react-intl';
import { type BlockEditProps } from '@plone/types';
import config from '@plone/volto/registry';
import { P } from '@plone/volto-slate/constants';
import './pagetitlewithmenu.scss';
import Menu from './menu';
import { type PageTitleWithMenuData } from './View';

const messages = defineMessages({
  title: {
    id: 'Type the title…',
    defaultMessage: 'Type the title…',
  },
});

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

type Props = BlockEditProps & {
  data: PageTitleWithMenuData;
};

export const EditPageTitleWithMenuBlock = (props: Props) => {
  const {
    block,
    blockNode,
    data,
    detached,
    editable,
    index,
    metadata,
    onAddBlock,
    onChangeField,
    onFocusNextBlock,
    onFocusPreviousBlock,
    onSelectBlock,
    properties,
    selected,
  } = props;
  const items = (properties?.items || []).filter(
    (item) => !item.exclude_from_nav,
  );
  const [editor] = useState(withReact(createEditor()));
  const contentId = properties?.id || '';
  const [initialValue] = useState([
    {
      type: P,
      children: [{ text: metadata?.title || properties?.title || '' }],
    },
  ]);

  const intl = useIntl();

  const prevSelected = usePrevious(selected);

  const text = useMemo(
    () => metadata?.title || properties?.title || '',
    [metadata, properties],
  );

  const placeholder = useMemo(
    () => data.placeholder || intl.formatMessage(messages.title),
    [data.placeholder, intl],
  );
  const disableNewBlocks = useMemo(() => detached, [detached]);

  useEffect(() => {
    if (!prevSelected && selected) {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        // keep selection
        ReactEditor.focus(editor);
      } else {
        // nothing is selected, move focus to end
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.end(editor, []));
      }
    }
  }, [prevSelected, selected, editor]);

  useEffect(() => {
    // undo/redo handlerr
    const oldText = Node.string(editor);
    if (oldText !== text) {
      Transforms.insertText(editor, text, {
        at: [0, 0],
      });
    }
  }, [editor, text]);

  const handleChange = useCallback(() => {
    const newText = Node.string(editor);
    if (newText !== text) {
      onChangeField('title', newText);
    }
  }, [editor, onChangeField, text]);

  const handleKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Return' || ev.key === 'Enter') {
        ev.preventDefault();
        if (!disableNewBlocks) {
          // @ts-expect-error
          onSelectBlock(
            onAddBlock(config.settings.defaultBlockType, index + 1),
          );
        }
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        // @ts-expect-error
        onFocusPreviousBlock(block, blockNode.current);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        // @ts-expect-error
        onFocusNextBlock(block, blockNode.current);
      }
    },
    [
      index,
      blockNode,
      disableNewBlocks,
      onSelectBlock,
      onAddBlock,
      onFocusPreviousBlock,
      onFocusNextBlock,
      block,
    ],
  );

  const handleFocus = useCallback(() => {
    // @ts-expect-error
    onSelectBlock(block);
  }, [block, onSelectBlock]);

  const renderElement = useCallback(
    ({ attributes, children }) => {
      return (
        <div className="product-title-wrapper">
          <div className="product-title">
            <h1 {...attributes} className="documentFirstHeading">
              {children}
            </h1>
          </div>
          <div className="product-navigation" contentEditable="plaintext-only">
            <Menu items={items} isEditMode={true} />
          </div>
        </div>
      );
    },
    [contentId],
  );

  if (typeof window.__SERVER__ !== 'undefined') {
    return <div />;
  }

  return (
    <>
      <Slate
        editor={editor}
        onChange={handleChange}
        initialValue={initialValue}
      >
        <Editable
          readOnly={!editable}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          renderElement={renderElement}
          onFocus={handleFocus}
          aria-multiline="false"
        ></Editable>
      </Slate>
    </>
  );
};

export default EditPageTitleWithMenuBlock;
